// src/components/common/UserMenu.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config';
import { LogOut, User, Settings, ChevronDown, Shield, LifeBuoy } from 'lucide-react';
import ContextAwareQuestionForm from '../../products/questions/components/ContextAwareQuestionForm';
import NotificationBell from './NotificationBell';

const UserMenu = ({ 
  user, 
  userProfile, 
  userRole
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSupportForm, setShowSupportForm] = useState(false);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error.message);
    }
  };

  const getUserDisplayName = () => {
    // Use username first (as requested)
    if (userProfile?.username) return userProfile.username;
    // Fallback to email if no username
    if (user?.email) return user.email.split('@')[0];
    return 'User';
  };

  // Display name is sourced from user_profiles table in this priority order:
  // 1. userProfile.first_name + userProfile.last_name (combined from user_profiles table)
  // 2. userProfile.first_name or userProfile.last_name (individual fields)
  // 3. userProfile.username (from user_profiles table)
  // 4. user.email.split('@')[0] (fallback from email)

  const getUserInitials = () => {
    const name = getUserDisplayName();
    // For username (single word), just use first character
    // If it's email-based, use first character
    if (name.includes(' ')) {
      const parts = name.split(' ');
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const handleMyAccountNavigation = () => {
    setShowUserMenu(false);
    navigate('/my-account/dashboard');
  };

  const handleProfileNavigation = () => {
    setShowUserMenu(false);
    navigate('/my-account/profile');
  };

  const handleSubscriptionsNavigation = () => {
    setShowUserMenu(false);
    navigate('/my-account/subscriptions');
  };

  const handleAskSupport = () => {
    setShowUserMenu(false);
    setShowSupportForm(true);
  };

  // Get role display info
  const getRoleDisplay = () => {
    if (!userProfile) return null;
    
    let roleKey = 'viewer';
    if (userProfile.is_super_admin) roleKey = 'super_admin';
    else if (userProfile.subscription_status === 'creator') roleKey = 'creator';
    else if (userProfile.subscription_status === 'support') roleKey = 'support';
    
    const roleConfig = {
      'super_admin': { label: 'Super Admin', color: 'bg-purple-100 text-purple-800', icon: true },
      'creator': { label: 'Creator', color: 'bg-blue-100 text-blue-800', icon: false },
      'support': { label: 'Support', color: 'bg-green-100 text-green-800', icon: false },
      'viewer': { label: 'Viewer', color: 'bg-gray-100 text-gray-800', icon: false }
    };

    return roleConfig[roleKey] || roleConfig['viewer'];
  };

  const roleDisplay = getRoleDisplay();

  return (
    <div className="relative flex items-center space-x-2">
      {/* Notification Bell */}
      {user?.id && <NotificationBell userId={user.id} />}
      
      <button
        onClick={() => setShowUserMenu(!showUserMenu)}
        className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
      >
        <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-semibold">
          {getUserInitials()}
        </div>
        <span className="hidden sm:block">{getUserDisplayName()}</span>
        <ChevronDown className="h-4 w-4 text-gray-500" />
      </button>

      {/* User Dropdown Menu */}
      {showUserMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowUserMenu(false)}
          />
          <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50">
            <div className="p-3 border-b border-gray-100">
              <p className="font-medium text-gray-900 truncate">
                {getUserDisplayName()}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              {/* Role display */}
              {roleDisplay && (
                <div className="mt-2">
                  <span className={`text-xs px-2 py-1 rounded-full inline-flex items-center ${roleDisplay.color}`}>
                    {roleDisplay.icon && <Shield className="h-3 w-3 mr-1" />}
                    {roleDisplay.label}
                  </span>
                </div>
              )}
            </div>
            <nav className="p-1 space-y-1">
              <button
                onClick={handleMyAccountNavigation}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md transition-colors"
              >
                <User className="h-4 w-4 mr-3" />
                My Account
              </button>

              <button
                onClick={handleProfileNavigation}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md transition-colors"
              >
                <Settings className="h-4 w-4 mr-3" />
                Profile
              </button>

              <button
                onClick={handleSubscriptionsNavigation}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md transition-colors"
              >
                <Settings className="h-4 w-4 mr-3" />
                Subscriptions
              </button>

              <button
                onClick={handleAskSupport}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md transition-colors"
              >
                <LifeBuoy className="h-4 w-4 mr-3" />
                Ask Support
              </button>

              <div className="border-t border-gray-100 my-1"></div>

              <button
                onClick={handleSignOut}
                className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
              >
                <LogOut className="h-4 w-4 mr-3" />
                Logout
              </button>
            </nav>
          </div>
        </>
      )}
      <ContextAwareQuestionForm
        isOpen={showSupportForm}
        onClose={() => setShowSupportForm(false)}
        context={{
          moduleType: 'general',
          moduleReferenceId: null,
          moduleReferenceName: null,
          projectId: null,
          companyId: null
        }}
        user={user}
      />
    </div>
  );
};

export default UserMenu;



